import { Translate } from '@frontend/translation';
import { Typography } from '@mui/material';
import { useIntl } from 'react-intl';
import { TextField } from '@frontend/ui-elements';
import { Button } from '@frontend/ui-elements';
import * as yup from 'yup';
import {
  IUserLoginErrors,
  IUserLoginKeys,
  IUserLoginRequest,
  IUserRoleType,
} from '@frontend/api';
import { SubmitHandler, useForm } from 'react-hook-form';
import FormType from '@customer-platform-shared/components/Forms/utils/FormType.types';
import { yupResolver } from '@hookform/resolvers/yup';
import { useFormTracking } from '@customer-platform-shared/components/Forms/utils/useFormTracking';
import { LoginFormBox } from './LoginForm.css';
import { useEffect } from 'react';

type LoginFormProps = {
  onSubmitHandler: SubmitHandler<FormType<IUserLoginRequest>>;
  isSuccess: boolean;
  isPending: boolean;
  loginErrors?: IUserLoginErrors;
};

export default function LoginForm({
  onSubmitHandler,
  isSuccess,
  isPending,
  loginErrors,
}: LoginFormProps) {
  const intl = useIntl();

  const requiredField = intl.formatMessage({
    id: 'customer-platform.shared.components.forms.errors.required',
  });

  const formId = 'login-form.form';

  const loginFormSchema = yup.object().shape({
    email: yup
      .string()
      .email(
        intl.formatMessage({
          id: 'customer-platform.shared.components.forms.errors.email.invalid',
        }),
      )
      .required(requiredField),
    role: yup.mixed<IUserRoleType>().optional(),
  });

  const {
    register,
    handleSubmit,
    setError,
    formState: { errors: formErrors },
  } = useForm<FormType<IUserLoginRequest>>({
    defaultValues: {
      email: '',
    },
    resolver: yupResolver<FormType<IUserLoginRequest>>(loginFormSchema),
  });

  useEffect(() => {
    if (loginErrors !== undefined) {
      Object.keys(loginErrors).forEach(field =>
        setError(field as IUserLoginKeys, {
          type: 'manual',
          message: Array.isArray(loginErrors[field as IUserLoginKeys])
            ? (loginErrors[field as IUserLoginKeys][0] as string)
            : (loginErrors[field as IUserLoginKeys] as unknown as string),
        }),
      );
    }
  }, [loginErrors, setError]);

  useFormTracking({ formId, formErrors, isSuccess });

  return (
    <LoginFormBox>
      <form
        onSubmit={handleSubmit(onSubmitHandler)}
        noValidate
        id={formId}
        name={formId}
      >
        <Typography variant="p3">
          <Translate id="customer-platform.shared.login.login-form.header" />
        </Typography>
        <TextField
          id="email"
          type="email"
          aria-label={intl.formatMessage({
            id: 'customer-platform.shared.login.login-form.textfield.email',
          })}
          label={
            <Translate id="customer-platform.shared.login.login-form.textfield.email" />
          }
          {...register('email')}
          error={!!formErrors?.email}
          helperText={formErrors?.email?.message}
          required
          formId={formId}
        />

        <Button
          buttonType="primary"
          id="login-modal.button.submit"
          size="large"
          type="submit"
          isSubmitting={isPending}
        >
          <Translate id="customer-platform.shared.login.login-form.button.login" />
        </Button>
      </form>
    </LoginFormBox>
  );
}
